import { Plus, Search, X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { filterBySearch } from "@/shared/utils/text-search";

/** O núcleo inativo aparece na lista de vínculo mas não se escolhe
 *  (2026-10-23): o servidor recusa o vínculo, e um núcleo inativo não dá
 *  acesso a empresa nenhuma. */
export function inactiveGroupReason(group: { is_active: boolean }): string | null {
  return group.is_active ? null : "Núcleo inativo: reative-o em Núcleos para vincular.";
}

/** Quantas sugestões a caixa mostra de uma vez — o resto aparece digitando. */
const MAX_SUGGESTIONS = 50;
/** A partir de quantos vínculos a lista de vinculados ganha o próprio filtro. */
const LINKED_FILTER_FROM = 8;

type Placement = { top: number; left: number; width: number; maxHeight: number; up: boolean };

/** Onde a lista de sugestões abre, medida na tela: embaixo do campo, ou em
 *  cima quando embaixo não cabe (a forma do `Select` da casa). */
function placementFor(anchor: HTMLElement): Placement {
  const rect = anchor.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom - 12;
  const above = rect.top - 12;
  const up = below < 200 && above > below;
  return {
    top: up ? rect.top - 6 : rect.bottom + 6,
    left: rect.left,
    width: rect.width,
    maxHeight: Math.max(140, Math.min(280, up ? above : below)),
    up
  };
}

/**
 * Uma lista de vínculos do detalhe de Usuários e de Núcleos (núcleos,
 * empresas, usuários): buscar digitando + "Adicionar", e "Remover" em
 * cada linha.
 *
 * Era escrita duas vezes, igual, em `UsersAdmin` e `CompanyGroupsAdmin`
 * (2026-10-14: unificada ao redesenhar as duas telas). O fluxo continua
 * de dois passos — escolher e confirmar — porque vínculo dá acesso a
 * dados de cliente, e um clique distraído na lista não deve concedê-lo.
 *
 * A escolha era um `<select>` nativo (2026-10-15: virou busca por
 * digitação, pedido da dona): com centenas de empresas, rolar a caixa
 * atrás de um nome não funcionava. A busca é a mesma das listas de
 * cadastro (`filterBySearch`): sem acento, sem caixa, palavras em
 * qualquer ordem, e olha também o `detail` (o e-mail, na pessoa).
 * Teclado: setas andam, Enter escolhe — e, com a escolha feita, Enter
 * adiciona —, Esc fecha.
 *
 * A lista de sugestões abre num PORTAL no `body`, com `position: fixed`
 * (2026-10-19): o cartão de detalhe rola sozinho, e uma lista dentro dele
 * ficava cortada perto da borda de baixo. Quem liga campo e lista é o
 * `aria-controls`.
 */
export function AccessLinkList<T extends { id: number | string }>({
  title,
  hint,
  available,
  linked,
  label,
  detail,
  searchPlaceholder,
  icon,
  onGrant,
  onRevoke,
  readOnly = false,
  isActive,
  statusLabels = ["Ativa", "Inativa"],
  unavailable
}: {
  title: string;
  /** Uma linha dizendo o que o vínculo dá (ex.: "Enxerga todas as empresas de cada núcleo."). */
  hint?: string;
  available: T[];
  linked: T[];
  label: (item: T) => string;
  /** Segunda linha da sugestão e do vinculado (ex.: o e-mail); também entra na busca. */
  detail?: (item: T) => string | null | undefined;
  /** Ex.: "Digite o nome da empresa". */
  searchPlaceholder: string;
  /** O ícone de cada linha (núcleo, empresa, pessoa). */
  icon: ReactNode;
  onGrant: (id: T["id"]) => Promise<void>;
  onRevoke: (id: T["id"]) => Promise<void>;
  /** Só a lista, sem adicionar nem remover — quem vê o vínculo mas não o
   *  dá (o Administrador olhando as pessoas do núcleo, RN-05). */
  readOnly?: boolean;
  /** Ativo ou inativo (2026-10-23, pedido da dona): uma bolinha verde ou
   *  cinza antes do nome, nas sugestões e nos vinculados. Sem a função,
   *  nada aparece (a lista de pessoas não tem esse estado). A lista mostra
   *  os dois — quem escolhe é a pessoa, vendo a situação. */
  isActive?: (item: T) => boolean | undefined;
  /** Os nomes da situação, pro leitor de tela e pro tooltip: "Ativa"/
   *  "Inativa" (empresa) ou "Ativo"/"Inativo" (núcleo). */
  statusLabels?: [string, string];
  /** O item aparece mas não pode ser escolhido, e o texto diz por quê
   *  (2026-10-23: o núcleo inativo, que o servidor recusa e que não daria
   *  acesso a nada). `null`/`undefined` = escolhível. */
  unavailable?: (item: T) => string | null | undefined;
}) {
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<T | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [linkedQuery, setLinkedQuery] = useState("");
  const [placement, setPlacement] = useState<Placement | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const fields = (item: T) => [label(item), detail?.(item)];
  // Com a escolha feita, o campo mostra o nome dela — filtrar por ele
  // esconderia as outras opções se a pessoa reabrir a lista.
  const matches = filterBySearch(available, chosen ? "" : query, fields);
  const suggestions = matches.slice(0, MAX_SUGGESTIONS);
  const hidden = matches.length - suggestions.length;
  const visibleLinked = filterBySearch(linked, linkedQuery, fields);
  const optionId = (index: number) => `${listId}-option-${index}`;

  const pick = (item: T) => {
    if (unavailable?.(item)) return;
    setChosen(item);
    setQuery(label(item));
    setOpen(false);
  };

  const clear = () => {
    setChosen(null);
    setQuery("");
    setActive(0);
  };

  const add = () => {
    if (!chosen) return;
    void onGrant(chosen.id);
    clear();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) setOpen(true);
      else setActive(index => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(index => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (open && suggestions[active]) pick(suggestions[active]);
      else if (chosen) add();
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    }
  };

  /** A bolinha de situação, quando a lista sabe a situação do item. */
  const statusDot = (item: T) => {
    const active = isActive?.(item);
    if (active === undefined) return null;
    const text = active ? statusLabels[0] : statusLabels[1];
    return (
      <span
        className={`access-status-dot ${active ? "is-active" : "is-inactive"}`}
        role="img"
        aria-label={text}
        title={text}
      />
    );
  };

  const nothingLeft = available.length === 0;
  const showList = open && !nothingLeft;

  // Medida ao abrir, e de novo quando a página ou o cartão rolam e quando a
  // janela muda: a lista acompanha o campo. Rolar a própria lista não conta.
  useLayoutEffect(() => {
    if (showList && pickerRef.current) setPlacement(placementFor(pickerRef.current));
  }, [showList]);
  useEffect(() => {
    if (!showList) return;
    const follow = (event?: Event) => {
      if (event?.target instanceof Node && listRef.current?.contains(event.target)) return;
      if (pickerRef.current) setPlacement(placementFor(pickerRef.current));
    };
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    return () => {
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
  }, [showList]);

  return (
    <div className="access-links">
      <div className="access-links-head">
        <h4>{title}</h4>
        <span className="access-links-count" title={`${linked.length} vinculados`}>
          {linked.length}
        </span>
      </div>
      {hint && <p className="access-links-hint">{hint}</p>}
      {!readOnly && <div className="access-link-add">
        <div ref={pickerRef} className={`access-link-picker ${open ? "is-open" : ""}`}>
          <Search size={15} aria-hidden="true" />
          <input
            type="text"
            role="combobox"
            aria-label={`Buscar para adicionar em ${title}`}
            aria-expanded={open && !nothingLeft}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open && suggestions[active] ? optionId(active) : undefined}
            autoComplete="off"
            spellCheck={false}
            value={query}
            disabled={nothingLeft}
            placeholder={nothingLeft ? "Nada mais para adicionar" : searchPlaceholder}
            onChange={event => {
              setQuery(event.target.value);
              setChosen(null);
              setActive(0);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKeyDown}
          />
          {query && (
            <button
              type="button"
              className="access-link-picker-clear"
              aria-label="Limpar busca"
              onMouseDown={event => event.preventDefault()}
              onClick={clear}
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
          {showList && createPortal(
            // `onMouseDown` com preventDefault: o clique na sugestão não
            // tira o foco do campo antes de escolher (o `onBlur` fecharia
            // a lista e o clique cairia no vazio).
            <ul
              ref={listRef}
              className={`access-link-suggestions${placement?.up ? " opens-up" : ""}`}
              id={listId}
              role="listbox"
              aria-label={title}
              style={placement ? {
                top: placement.up ? undefined : placement.top,
                bottom: placement.up ? window.innerHeight - placement.top : undefined,
                left: placement.left,
                width: placement.width,
                maxHeight: placement.maxHeight
              } : undefined}
              onMouseDown={event => event.preventDefault()}
            >
              {suggestions.map((item, index) => {
                const blocked = unavailable?.(item);
                return (
                  <li
                    key={item.id}
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === active}
                    aria-disabled={blocked ? true : undefined}
                    className={[index === active ? "is-active" : "", blocked ? "is-unavailable" : ""].join(" ").trim() || undefined}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => pick(item)}
                  >
                    <span className="access-link-icon" aria-hidden="true">{icon}</span>
                    {statusDot(item)}
                    <span className="access-link-suggestion-text">
                      <span>{label(item)}</span>
                      {(blocked || detail?.(item)) && <small>{blocked || detail?.(item)}</small>}
                    </span>
                  </li>
                );
              })}
              {suggestions.length === 0 && (
                <li className="access-link-suggestions-empty" role="presentation">
                  Nada encontrado para “{query.trim()}”.
                </li>
              )}
              {hidden > 0 && (
                <li className="access-link-suggestions-more" role="presentation">
                  Mais {hidden} — continue digitando para filtrar.
                </li>
              )}
            </ul>,
            document.body
          )}
        </div>
        <button className="secondary-button" type="button" disabled={!chosen} onClick={add}>
          <Plus size={15} aria-hidden="true" />
          Adicionar
        </button>
      </div>}
      {linked.length === 0 ? (
        <p className="access-links-empty">Nenhum vínculo.</p>
      ) : (
        <>
          {linked.length >= LINKED_FILTER_FROM && (
            <div className="access-links-filter">
              <Search size={14} aria-hidden="true" />
              <input
                type="search"
                aria-label={`Filtrar ${title} vinculados`}
                placeholder={`Filtrar os ${linked.length} vinculados`}
                value={linkedQuery}
                onChange={event => setLinkedQuery(event.target.value)}
              />
            </div>
          )}
          <ul>
            {visibleLinked.map(item => (
              <li key={item.id}>
                <span className="access-link-icon" aria-hidden="true">{icon}</span>
                {statusDot(item)}
                <span className="access-link-name">
                  {label(item)}
                  {detail?.(item) && <small>{detail(item)}</small>}
                </span>
                {!readOnly && (
                  <button type="button" className="access-link-remove" onClick={() => void onRevoke(item.id)}>
                    <X size={14} aria-hidden="true" />
                    Remover
                  </button>
                )}
              </li>
            ))}
            {visibleLinked.length === 0 && (
              <li className="access-links-filter-empty">Nenhum vinculado casa com “{linkedQuery.trim()}”.</li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}

/** O cabeçalho do detalhe (usuário ou núcleo): ícone, nome, uma linha de
 *  contexto e o selo de situação. */
export function AccessDetailHead({
  icon,
  title,
  subtitle,
  status,
  tone
}: {
  icon: ReactNode;
  title: string;
  subtitle?: ReactNode;
  status: string;
  tone: "success" | "warning" | "inactive" | "active";
}) {
  return (
    <div className="access-detail-head">
      <span className={`access-detail-icon is-${tone}`} aria-hidden="true">{icon}</span>
      <div className="access-detail-title">
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      <span className={`badge-pill ${tone}`}>{status}</span>
    </div>
  );
}
